import Avatar from "@components/avatar/Avatar";
import { SystemAvatarDefs } from "@components/avatar/SystemAvatarDef";
import FocusManager, { FocusDirection, IFocusableItemProps } from "@components/focusManager/FocusManager";
import BindingContext, { createPath } from "@odata/BindingContext";
import { CompanyEntity, FileMetadataEntity } from "@odata/GeneratedEntityTypes";
import { composeRefHandlers } from "@utils/general";
import memoizeOneWithParamsCheck from "@utils/memoizeOne";
import React from "react";

import { FILES_API_URL } from "../../constants";
import { AppContext } from "../../contexts/appContext/AppContext.types";
import { AvatarSize } from "../../enums";
import { KeyName } from "../../keyName";
import { FormStorage } from "../../views/formView/FormStorage";
import { AvatarWrapper, StyledAvatarSelector } from "./Company.styles";

interface IProps {
    storage: FormStorage;
}

interface IListAvatarProps {
    itemProps: IFocusableItemProps;
    onClick: (id: number) => void;
    onKeyDown: (e: React.KeyboardEvent, id: number) => void;
    isSelected: boolean;
    id: number;
}

class ListAvatar extends React.PureComponent<IListAvatarProps> {
    handleKeyDown = (e: React.KeyboardEvent) => {
        this.props.onKeyDown(e, this.props.id);
    };

    handleClick = () => {
        this.props.onClick(this.props.id);
    };

    render() {
        return (
            <AvatarWrapper
                {...this.props.itemProps}
                onClick={this.handleClick}
                onKeyDown={this.handleKeyDown}
                $isSelected={this.props.isSelected}>
                <Avatar
                    size={AvatarSize.XL}
                    src={`${FILES_API_URL}/${this.props.id}`}
                />
            </AvatarWrapper>
        );
    }
}

export default class CompanyAvatarSelector extends React.Component<IProps> {
    static contextType = AppContext;

    wrapperRef = React.createRef<HTMLDivElement>();

    get logoIdBc(): BindingContext {
        return this.props.storage.data.bindingContext.navigate(createPath(CompanyEntity.Logo, FileMetadataEntity.Id));
    }

    componentDidMount() {
        const currentId = this.props.storage.getValue(this.logoIdBc);
        const selectedIndex = this.getColumns().findIndex(id => id === currentId);

        // focus selected avatar
        (this.wrapperRef.current.children[selectedIndex] as HTMLDivElement).focus();
    }

    getColumns = memoizeOneWithParamsCheck(() => {
        return Object.values(SystemAvatarDefs).map(o => o.id);
    });

    handleAvatarClick = (value: number): void => {
        this.props.storage.setTemporalData(this.logoIdBc, { value });
        this.forceUpdate();
    };

    handleKeyDown = (e: React.KeyboardEvent, id: number): void => {
        if (e.key === KeyName.Enter || e.key === KeyName.Space) {
            this.handleAvatarClick(id);
        }
    };

    render() {
        const storage = this.props.storage;
        const currentId = storage.getTemporalData(this.logoIdBc)?.value ?? storage.getValue(this.logoIdBc);
        // dialog has hardcoded width("676px") so we can use grid focus direction with columnsCount
        return (
                <FocusManager direction={FocusDirection.Grid} columnsCount={6}>
                    {({ itemProps, wrapperProps }) => (
                            <StyledAvatarSelector {...wrapperProps}
                                                  ref={composeRefHandlers(wrapperProps.ref, this.wrapperRef)}>
                                {this.getColumns().map((id: number) => {
                                    return <ListAvatar
                                            key={id}
                                            itemProps={itemProps}
                                            onClick={this.handleAvatarClick}
                                            isSelected={currentId === id}
                                            onKeyDown={this.handleKeyDown}
                                id={id}
                            />;
                        })}
                    </StyledAvatarSelector>

                )}
            </FocusManager>
        );
    }
}